import { inject, Injectable } from "@angular/core";
import polylabel from 'polylabel';
import { TLayer } from "../../../types/explorer/sjmap2/TLayer";
import { MapLibreLayersService } from "../../shared/maplibreLayers.service";
import { AddLayerObject, GeoJSONSource, Map, RasterLayerSpecification, SourceSpecification, SymbolLayerSpecification } from "maplibre-gl";
import type { Feature, FeatureCollection, GeoJsonProperties } from "geojson";
import { ExplorerSjmapToMaplibreService } from "../explorer-sjmap-to-maplibre/explorer-sjmap-to-maplibre.service";
import { Store } from "@ngrx/store";
import { addMaplibreToRaster, addMaplibreToVector } from "../../../store/actions/explorer/explorer-sjmap.actions";
import { IRasterMapLibre, IVectorMapLibre } from "../../../interfaces/sjmap2/IMapLibre";
import { TMapLibreLayerId } from "../../../types/explorer/sjmap2/TMapLibreLayerId";
import { TVectorMapLibreLayerId } from "../../../types/explorer/sjmap2/TVectorMapLibre";
import { VectorPopupService } from "../vector-popup/vector-popup.service";

interface IMapLibreSourceInfo {
    name: string,
    sourceSpec: SourceSpecification
}

interface IBaseMapLibreInfo {
    source: IMapLibreSourceInfo
}

interface IRasterMapLibreInfo extends IBaseMapLibreInfo {
    layer: RasterLayerSpecification
}

interface IVectorMapLibreInfo extends IBaseMapLibreInfo {
    layers: AddLayerObject[]
}

@Injectable({
  providedIn: 'root'
})
export class ExplorerAddSJMapLayerToMapService {

    private store: Store = inject(Store);
    private maplibreLayersService: MapLibreLayersService = inject(MapLibreLayersService);
    private explorerSjmapToMaplibreService: ExplorerSjmapToMaplibreService = inject(ExplorerSjmapToMaplibreService);
    private vectorPopupService: VectorPopupService = inject(VectorPopupService);

    private lastMapLibreLayerId: TMapLibreLayerId = null; // Ultimo layer aggiunto alla mappa

    private readonly districtLabelHandlers = new WeakMap<Map, globalThis.Map<string, () => void>>();
    
    constructor() { }

    releaseMapHandlers(_map: Map): void {
        this.districtLabelHandlers
            .get(_map)
            ?.forEach((updateLabels) => _map.off('idle', updateLabels));
        this.districtLabelHandlers.delete(_map);
        this.vectorPopupService.releasePopups(_map);
    }

    addSJMapLayerToMap(_map: Map, SJMapLayer: TLayer, prevSJMapLayer?: TLayer) {

        if(SJMapLayer.type === 'raster') {

            const rasterMaplibreInfos: IRasterMapLibreInfo[] = this.explorerSjmapToMaplibreService.fromRasterLayerToMaplibre(SJMapLayer); // Calcola le info di maplibre per il layer raster

            if(prevSJMapLayer) {
                // if(!this.maplibreLayersService.hasMapLibreData(prevSJMapLayer)) {
                //     console.error(`Impossibile aggiungere il layer ${SJMapLayer.uuid} alla mappa: il layer precedente ${prevSJMapLayer.uuid} non ha dati di MapLibre. Layer precedente:`, prevSJMapLayer);
                //     return;
                // }

                // Prendi l'id del MapLibreLayer più in basso del SJMapLayer precedente (il MapLibreLayer che sta sotto a tutti gli altri)
                const prevSJMLayerBottomMLLayerId: TMapLibreLayerId = this.lastMapLibreLayerId // this.maplibreLayersService.getBottomMLLayerIdFromSJMLayer(prevSJMapLayer); // NON FUNZIONA PER PROBLEMI DI ASINCRONISMO. TODO: DA SISTEMARE

                this.addSJMapRasterLayerToMap(_map, rasterMaplibreInfos, prevSJMLayerBottomMLLayerId); // Aggiungi il layer raster sotto l'ultimo MapLibreLayer del SJMapLayer precedente
            } else {
                rasterMaplibreInfos[0].layer.layout.visibility = 'visible'; // Imposta la visibilità del primo layer raster a "visible"
                this.addSJMapRasterLayerToMap(_map, rasterMaplibreInfos); // Aggiungi il layer raster sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }

            const rasterMapLibre: IRasterMapLibre[] = rasterMaplibreInfos.map((RMLInfo: IRasterMapLibreInfo) => {
                return {
                    source: RMLInfo.source.name,
                    layer: RMLInfo.layer.id
                }
            })

            if(!rasterMapLibre?.[rasterMapLibre?.length-1]?.layer) {
                console.error(`Impossibile aggiungere il layer ${SJMapLayer.uuid} alla mappa: non sono stati trovati dati di MapLibre per il layer raster. rasterMapLibre:`, rasterMapLibre);
                return;
            }
            this.lastMapLibreLayerId = rasterMapLibre?.[rasterMapLibre.length - 1]?.layer ?? null; // Salva l'ultimo layer aggiunto alla mappa
            this.store.dispatch(addMaplibreToRaster( { layerUUID: SJMapLayer.uuid, rasterMaplibre: rasterMapLibre } ));

        } else if (SJMapLayer.type === 'vector') {

            const vectorMaplibreInfos: IVectorMapLibreInfo[] = this.explorerSjmapToMaplibreService.fromVectorLayerToMaplibre(SJMapLayer);

            if(prevSJMapLayer) {
                // if(!this.maplibreLayersService.hasMapLibreData(prevSJMapLayer)) {
                //     console.error(`Impossibile aggiungere il layer ${SJMapLayer.uuid} alla mappa: il layer precedente ${prevSJMapLayer.uuid} non ha dati di MapLibre. Layer precedente:`, prevSJMapLayer);
                //     return;
                // }

                // Prendi l'id del MapLibreLayer più in basso del SJMapLayer precedente (il MapLibreLayer che sta sotto a tutti gli altri)
                const prevSJMLayerBottomMLLayerId: TMapLibreLayerId = this.lastMapLibreLayerId // this.maplibreLayersService.getBottomMLLayerIdFromSJMLayer(prevSJMapLayer); // NON FUNZIONA PER PROBLEMI DI ASINCRONISMO. TODO: DA SISTEMARE

                this.addSJMapVectorLayerToMap(_map, vectorMaplibreInfos, prevSJMLayerBottomMLLayerId); // Aggiungi il layer vector sotto l'ultimo MapLibreLayer del SJMapLayer precedente
            } else {
                this.addSJMapVectorLayerToMap(_map, vectorMaplibreInfos); // Aggiungi il layer vector sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }

            const vectorMapLibre: IVectorMapLibre[] = vectorMaplibreInfos.map((VMLInfo: IVectorMapLibreInfo) => {
                return {
                    source: VMLInfo.source.name,
                    layers: VMLInfo.layers.map((l: AddLayerObject) => l.id)
                }
            });

            if(!vectorMapLibre?.[vectorMapLibre.length - 1]?.layers?.[vectorMapLibre?.[vectorMapLibre?.length - 1]?.layers?.length - 1]) {
                console.error(`Impossibile aggiungere il layer ${SJMapLayer.uuid} alla mappa: non sono stati trovati dati di MapLibre per il layer vector. Layer:`, SJMapLayer);
                return;
            }
            this.lastMapLibreLayerId = vectorMapLibre[vectorMapLibre.length - 1].layers[vectorMapLibre[vectorMapLibre.length - 1].layers.length - 1] ?? null; // Salva l'ultimo layer aggiunto alla mappa
            this.store.dispatch(addMaplibreToVector( { layerUUID: SJMapLayer.uuid, vectorMaplibre: vectorMapLibre } ));

        }
        
    }

    private addSJMapRasterLayerToMap(_map: Map, rasterMLInfos: IRasterMapLibreInfo[], upperMapLibreLayerId?: TMapLibreLayerId) {

        rasterMLInfos.forEach((rasterMLInfo: IRasterMapLibreInfo, index, rasterMLInfos: IRasterMapLibreInfo[]) => {
            rasterMLInfo.layer.paint = {"raster-opacity": 0.8};

            this.addRasterSourceToMap(_map, rasterMLInfo.source);

            const prevInfo = rasterMLInfos[index - 1] ?? null; // Prendi l'info precedente o null se non c'è
            if(prevInfo) {
                this.addRasterLayerToMap(_map, rasterMLInfo.layer, prevInfo.layer.id); // Aggiungi il layer dell'info attuale sotto il layer dell'info precedente
            } else if(upperMapLibreLayerId) {
                this.addRasterLayerToMap(_map, rasterMLInfo.layer, upperMapLibreLayerId); // Aggiungi il layer dell'info attuale sotto il layer precedente nella SJMap
            } else {
                this.addRasterLayerToMap(_map, rasterMLInfo.layer) // Aggiungi il layer sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }

        })

    }

    private addRasterSourceToMap(_map: Map, rasterSource: IMapLibreSourceInfo) {
        this.maplibreLayersService.addSourceToMap(_map, rasterSource.name, rasterSource.sourceSpec)
    }

    // Se cambia la struttura del layer servirà
    // private addRasterLayersToMap(_map: Map, rasterLayers: AddLayerObject[], upperMapLibreLayerId?: TMapLibreLayerId) {
    // 
    //     rasterLayers.forEach((rasterLayer: AddLayerObject, index, rasterLayers: AddLayerObject[]) => {
    //         const prevLayer = rasterLayers[index - 1] ?? null; // Prendi il layer precedente o null se non c'è
    //         if(prevLayer) {
    //             this.addRasterLayerToMap(_map, rasterLayer, prevLayer.id) // Aggiungi il layer sotto il layer precedente
    //         } else if(upperMapLibreLayerId) {
    //             this.addRasterLayerToMap(_map, rasterLayer, upperMapLibreLayerId) // Aggiungi il layer sotto il layer precedente nella SJMap
    //         } else {
    //             this.addRasterLayerToMap(_map, rasterLayer) // Aggiungi il layer sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
    //         }
    //     });
    // 
    // }

    private addRasterLayerToMap(_map: Map, rasterLayer: AddLayerObject, upperMapLibreLayerId?: TMapLibreLayerId) {
        if(upperMapLibreLayerId) {
            this.maplibreLayersService.addLayerToMap(_map, rasterLayer, upperMapLibreLayerId)
        } else {
            this.maplibreLayersService.addLayerToMap(_map, rasterLayer)
        }
    }

    private addSJMapVectorLayerToMap(_map: Map, vectorMLInfos: IVectorMapLibreInfo[], upperMapLibreLayerId?: TMapLibreLayerId) {

        vectorMLInfos.forEach((vectorMLInfo: IVectorMapLibreInfo, index, vectorMLInfos: IVectorMapLibreInfo[]) => {

            this.addVectorSourceToMap(_map, vectorMLInfo.source);

            const prevInfo = vectorMLInfos[index - 1] ?? null; // Prendi l'info precedente o null se non c'è
            if(prevInfo) {
                const prevInfoBottomId: TVectorMapLibreLayerId = prevInfo.layers[prevInfo.layers.length - 1].id; // Prendi l'id del layer più in basso della info precedente
                this.addVectorLayersToMap(_map, vectorMLInfo.layers, prevInfoBottomId); // Aggiungi i layer dell'info attuale sotto il layer dell'info precedente
            } else if(upperMapLibreLayerId) {
                this.addVectorLayersToMap(_map, vectorMLInfo.layers, upperMapLibreLayerId); // Aggiungi i layer dell'info attuale sotto il layer precedente nella SJMap
            } else {
                this.addVectorLayersToMap(_map, vectorMLInfo.layers); // Aggiungi i layer sopra a "TUTTI GLI ALTRI" (ci saranno ulteriori check a valle)
            }

            const fillLayerId = vectorMLInfo.layers[0]?.id;
            const sourceLayer = (vectorMLInfo.layers[0] as { 'source-layer'?: string })?.['source-layer'] as string;
            if (fillLayerId && sourceLayer) {
                this.scheduleDistrictLabels(_map, vectorMLInfo.source.name, sourceLayer, fillLayerId);
            }

        });

    }

    private scheduleDistrictLabels(_map: Map, sourceId: string, sourceLayer: string, fillLayerId: string): void {
        const labelLayerId = `${fillLayerId}-label`;
        const labelSourceId = `${fillLayerId}-label-source`;
        const frozen: Record<string, { coords: [number, number]; properties: GeoJsonProperties }> = {};

        const updateLabels = () => {
            if (!_map.getSource(sourceId)) return;

            const features = _map.querySourceFeatures(sourceId, { sourceLayer });

            // Group all visible fragments per name, keeping the largest by bbox area.
            // querySourceFeatures returns clipped tile fragments, so multiple fragments
            // exist per district. polylabel on the largest fragment gives the best centroid.
            const best: Record<string, { rings: number[][][]; area: number; properties: GeoJsonProperties }> = {};

            features
                .filter((f) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
                .forEach((f) => {
                    const name = f.properties?.['name_eng'] || f.properties?.['name'] || f.properties?.['wijk_eng'];
                    if (!name || name in frozen) return;

                    let rings: number[][][];
                    if (f.geometry.type === 'Polygon') {
                        rings = f.geometry.coordinates;
                    } else if (f.geometry.type === 'MultiPolygon') {
                        rings = f.geometry.coordinates.reduce(
                            (acc: number[][][], poly: number[][][]) =>
                                poly[0].length > acc[0].length ? poly : acc
                        );
                    }

                    const outer = rings[0];
                    const xs = outer.map((c: number[]) => c[0]);
                    const ys = outer.map((c: number[]) => c[1]);
                    const area = (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));

                    if (!best[name] || area > best[name].area) {
                        best[name] = { rings, area, properties: f.properties };
                    }
                });

            let changed = false;
            for (const [name, { rings, properties }] of Object.entries(best)) {
                try {
                    const [lon, lat] = polylabel(rings, 0.001);
                    frozen[name] = { coords: [lon, lat], properties };
                    changed = true;
                } catch (e) {
                    console.warn(`[labels] polylabel failed for "${name}"`, e);
                }
            }

            if (!changed) return;

            const points: Feature[] = Object.values(frozen).map(({ coords, properties }) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: coords },
                properties,
            }));

            const geojson: FeatureCollection = { type: 'FeatureCollection', features: points };
            const existingSource = _map.getSource(labelSourceId) as GeoJSONSource;
            if (existingSource) {
                existingSource.setData(geojson);
            } else {
                try {
                    _map.addSource(labelSourceId, { type: 'geojson', data: geojson });
                    _map.addLayer({
                        id: labelLayerId,
                        type: 'symbol',
                        source: labelSourceId,
                        minzoom: 9,
                        layout: {
                            'text-field': ['coalesce', ['get', 'name_eng'], ['get', 'name'], ['get', 'wijk_eng']],
                            'text-font': ['Open Sans Semibold'],
                            'text-size': 12,
                            'text-anchor': 'center',
                            'text-allow-overlap': true,
                        },
                        paint: {
                            'text-color': '#ffffff',
                            'text-halo-color': '#000000',
                            'text-halo-width': 1.5,
                            'text-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0, 10, 1],
                        }
                    } as SymbolLayerSpecification);
                } catch(e) {
                    console.error('[labels] addLayer failed:', e);
                }
            }
        };

        let labelHandlers = this.districtLabelHandlers.get(_map);
        if (!labelHandlers) {
            labelHandlers = new globalThis.Map();
            this.districtLabelHandlers.set(_map, labelHandlers);
        }
        const previousUpdateLabels = labelHandlers.get(fillLayerId);
        if (previousUpdateLabels) {
            _map.off('idle', previousUpdateLabels);
        }
        _map.on('idle', updateLabels);
        labelHandlers.set(fillLayerId, updateLabels);
    }

    private addVectorSourceToMap(_map: Map, vectorSource: IMapLibreSourceInfo) {
        this.maplibreLayersService.addSourceToMap(_map, vectorSource.name, vectorSource.sourceSpec);
    }

    private addVectorLayersToMap(_map: Map, vectorLayers: AddLayerObject[], upperMapLibreLayerId?: TMapLibreLayerId) {

        vectorLayers.forEach((vectorLayer: AddLayerObject, index, vectorLayers: AddLayerObject[]) => {
            const prevLayer = vectorLayers[index - 1] ?? null;
            if(prevLayer) {
                this.addVectorLayerToMap(_map, vectorLayer, prevLayer.id)
            } else if(upperMapLibreLayerId) {
                this.addVectorLayerToMap(_map, vectorLayer, upperMapLibreLayerId)
            } else {
                this.addVectorLayerToMap(_map, vectorLayer)
            }
        });

    }

    private addVectorLayerToMap(_map: Map, vectorLayer: AddLayerObject, upperMapLibreLayerId?: TMapLibreLayerId) {
        if(upperMapLibreLayerId) {
            this.maplibreLayersService.addLayerToMap(_map, vectorLayer, upperMapLibreLayerId)
        } else {
            this.maplibreLayersService.addLayerToMap(_map, vectorLayer)
        }

        this.vectorPopupService.addPopup(_map, vectorLayer.id); // To do, aggiungere type dal layer

    }
    
}
